/* eslint-disable no-secrets/no-secrets */
import React, { useContext, useCallback, useEffect } from 'react';
import PrintDocumentsComponent from "premier-capability-policyjob-react/components/PrintDocumentsComponent/PrintDocumentsComponent";
import _ from 'lodash';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { WizardPage, wizardProps } from '@xengage/gw-portals-wizard-react';
import { UWIssuesUtil } from "premier-policycommon-util-js";
import VehicleCoverage from "gw-capability-quoteandbind-PremierRV-react/components/VehicleCoverage/VehicleCoverage"
import { DatatableUtil } from '@xengage/gw-portals-util-js';
import { useTranslator } from '@jutro/locale';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import FormsComponent from "premier-capability-policyjob-react/components/FormComponent/FormComponent";
import QuoteValidityComponent from "premier-capability-policyjob-react/components/QuoteValidityComponent/QuoteValidityComponent";
import QuoteDetailsComponent from 'premier-capability-policyjob-react/components/QuoteDetailsComponent/QuoteDetailsComponent';
import styles from './QuotePage.module.scss';
import presentationMetadata from './QuotePage.metadata.json5';
import messages from './QuotePage.messages';
import CoveragesPageComponent from "../Coverages/CoveragesPage";
import { BreakpointTrackerContext } from '@jutro/legacy/layout';

function QuotePage(props) {
    const {
        wizardData: submissionVM,
        steps,
        jumpTo,
        currentStep,
        wizardData, updateWizardData,
        underwritingIssues
    } = props;

    const breakpoint = useContext(BreakpointTrackerContext);
    const { pageKey } = currentStep;
    const isQuoteBlocked = UWIssuesUtil.isQuoteBlocked(underwritingIssues);
    const findBlockingPoint = UWIssuesUtil.findBlockingPoint(underwritingIssues);
    const underWritingIssuesNonBlocking = UWIssuesUtil.UWIssuesNonBlocking(underwritingIssues);
    const translator = useTranslator();
    const { authHeader } = useAuthentication();
    const selectedOffering = _.get(submissionVM.value, 'lobData.cybLiaLine.selectedOffering_Ext');
    let coverableName;

    if(selectedOffering === 'CybLiaPer') {
        coverableName = 'personalCoverable';
    } else if(selectedOffering === 'CybLiaCom') {
        coverableName = 'commercialCoverable';
    } 

    // eslint-disable-next-line security/detect-object-injection
    const metadata = presentationMetadata[pageKey];

    const handleNext = useCallback(
        () => {
            const offeredQuotes = _.get(submissionVM.value, 'quoteData.offeredQuotes[0].publicID');

            _.set(submissionVM, 'bindData.chosenQuote', offeredQuotes);

            return submissionVM;
        },
        [submissionVM]
    );

    const navigateToCoverages = useCallback(
        () => {
            const indexOfGeneralCoverages = _.findIndex(
                steps,
                ({ path }) => path === '/liabilityCoveragesPage'
            );

            jumpTo(indexOfGeneralCoverages);
        },
        [jumpTo, steps]
    );

    // eslint-disable-next-line arrow-body-style
    const getCell = (item, _index, property) => {
        return item[property.id];
    };

    const getFormTableData = () => _.map(submissionVM.value.lobData.cybLiaLine.lineCoverages.forms_Ext, (form) => ({
        formDes: form.formDes,
        formCategory: form.formCategory
    }));

    const getInternalUseOnlyConditionsOfQuoteTableData = () => _.map(_.get(submissionVM.value, `lobData.cybLiaLine.coverables.${coverableName}.internalUse.plesubofQuoExps`), (plesubofQuoExp) => ({
        conFieConditionsOfQuote: plesubofQuoExp.coFie
    }));

    const getInternalUseOnlyAdditionalConditionsTableData = () => _.map(_.get(submissionVM.value, `lobData.cybLiaLine.coverables.${coverableName}.internalUse.pleaddConofPolExps`), (pleaddConofPolExp) => ({
        conFieAdditionalConditions: pleaddConofPolExp.coFie
    }));

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            navigateToCoverages,
            getCell,
            sortString: DatatableUtil.sortString,
        },
        resolveComponentMap: {
            vehiclecoverage: VehicleCoverage,
            coveragespagecomponent: CoveragesPageComponent,
            quoteDetailsComponent: QuoteDetailsComponent,
            quoteValidityComponent: QuoteValidityComponent,
            PrintDocumentsComponent,
            FormsComponent
        }
    };

    const overrideProps = {
        '@field': {
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top',
        },
        underWritingIssues: {
            underwritingIssues: underWritingIssuesNonBlocking,
            blockQuote: findBlockingPoint,
            filterUWIssuesInCustomOffering: false,
            quoteID: _.get(submissionVM.value, 'quoteID'),
            accountNumber: _.get(submissionVM.value, 'baseData.accountNumber')
        },
        liabilityCoverageInfoComponent: {
            wizardData,
            updateWizardData,
            clausesBasePath: `lobData.cybLiaLine.lineCoverages.coverages`,
            title: "Coverages",
            clasueType: 'coverages',
            isEditable: false
        },
        quoteValidityComponent: {
            wizardData
        },
        quoteDetailsComponent: {
            wizardData
        },
        tableFormSummary: {
            data: getFormTableData()
        },
        policyQuoteValDaysHead: {
            content: translator(messages.clQuoteValidityDays, {
                quoteValidityDays: _.get(submissionVM, `baseData.quoteValidityDays_Ext.value`)
            })
        },
        taxes: {
            visible: _.get(submissionVM.value, 'baseData.baseState_Ext') === 'QC',
        },
        fees: {
            visible: _.get(submissionVM.value, 'quoteData.offeredQuotes') &&
                _.get(submissionVM.value, 'quoteData.offeredQuotes[0].premium.fees.amount') !== 0
        },
        policyfees: {
            visible: _.get(submissionVM.value, 'baseData.policyFee_Ext.amount') !== 0
        },
        inspectionFee: {
            visible: _.get(submissionVM.value, 'baseData.inspectionFee_Ext.amount') !== 0
        },
        extensionFee: {
            visible: _.get(submissionVM.value, 'baseData.extensionFee_Ext.amount') !== 0
        },
        PrintDocumentsComponent: {
            visible: submissionVM.value.baseData.periodStatus === 'Quoted',
            jobNumber: submissionVM.value.quoteID,
            authHeader
        },
        FormsComponent: {
            forms: submissionVM.value.lobData.cybLiaLine.lineCoverages.forms_Ext
        },
        tableInternalUseOnlyConditionsOfQuote: {
            data: getInternalUseOnlyConditionsOfQuoteTableData()
        },
        tableInternalUseOnlyAdditionalConditions: {
            data: getInternalUseOnlyAdditionalConditionsTableData()
        },
        internalUseOnlyConditionsOfQuoteContainer: {
            visible: _.get(submissionVM.value, `lobData.cybLiaLine.coverables.${coverableName}.internalUse.plesubofQuoExps`)?.length > 0
        },
        internalUseOnlyAdditionalConditionsContainer: {
            visible: _.get(submissionVM.value, `lobData.cybLiaLine.coverables.${coverableName}.internalUse.pleaddConofPolExps`)?.length > 0
        }   
    };

    useEffect(() => { }, []);

    return (
        <WizardPage
            disableNext={underWritingIssuesNonBlocking?.length > 0}
            onNext={handleNext}
        >
            <ViewModelForm
                uiProps={metadata.content}
                model={submissionVM}
                overrideProps={overrideProps}
                callbackMap={resolvers.resolveCallbackMap}
                classNameMap={resolvers.resolveClassNameMap}
                componentMap={resolvers.resolveComponentMap}
            />
        </WizardPage>
    );
}

QuotePage.propTypes = wizardProps;
export default QuotePage;
